import type { TeamQueue } from './team-check-in';

export interface DeskShooter {
 shooterId: string; name: string; bib: string | null; checkedInAt: string | null;
 teamId: string | null; teamName: string | null; status: string;
 canUndoCheckIn?: boolean; lane: number | null; queuePlace: number | null;
}
export interface DeskTeamCard {
 id: string; name: string; sortOrder: number; members: DeskShooter[];
 checkedIn: DeskShooter[]; pending: DeskShooter[]; complete: boolean; callerLabel?: string; callerTier: number; callerPosition: number;
}

/** Derive attendance from the full roster, never caller progress or score status. */
export function deskTeamCards(shooters: readonly DeskShooter[], queue: TeamQueue): DeskTeamCard[] {
 const teams = new Map([...queue.waiting, ...queue.active, ...(queue.completed ?? []), ...queue.incomplete].map(team => [team.teamId, team]));
 const groups = new Map<string, { name: string; sortOrder: number; members: DeskShooter[] }>();
 for (const team of teams.values()) groups.set(team.teamId, { name: team.name, sortOrder: team.sortOrder, members: [] });
 const seen = new Set<string>();
 for (const person of shooters) {
  if (seen.has(person.shooterId)) continue;
  seen.add(person.shooterId);
  const key = person.teamId ?? 'unassigned';
  if (!groups.has(key)) groups.set(key, {name: person.teamName ?? 'Not in a team', sortOrder: Number.MAX_SAFE_INTEGER, members: []});
  groups.get(key)!.members.push(person);
 }
 return [...groups].map(([id, group]) => {
  const checkedIn = group.members.filter(person => person.checkedInAt != null);
  const pending = group.members.filter(person => person.checkedInAt == null);
  const waitingIndex = queue.waiting.findIndex(team => team.teamId === id);
  const active = queue.active.some(team => team.teamId === id);
  const done = queue.completed?.some(team => team.teamId === id);
  const callerLabel = active ? 'Now shooting' : done ? 'Done' : waitingIndex === 0 ? 'Up next' : waitingIndex > 0 ? `Following #${waitingIndex}` : undefined;
  const callerTier = active ? 2 : done ? 3 : waitingIndex >= 0 ? 0 : 1;
  return {id,...group,checkedIn,pending,complete: pending.length === 0,callerLabel,callerTier,callerPosition:waitingIndex};
 }).sort((a,b) => a.callerTier - b.callerTier || (a.callerTier === 0 ? a.callerPosition - b.callerPosition : 0) || a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, undefined, {numeric:true}));
}

export type DeskFilter = 'pending' | 'complete' | 'all';
export function filterDeskCards(cards: readonly DeskTeamCard[], filter: DeskFilter): DeskTeamCard[] {
 return cards.filter(card => filter === 'all' || (filter === 'complete' ? card.complete : !card.complete));
}
export function deskSummary(cards: readonly DeskTeamCard[]) {
 const teams = cards.filter(card => card.id !== 'unassigned');
 return {ready:teams.filter(card => card.complete).length, teams:teams.length, checkedIn:cards.reduce((sum,card)=>sum+card.checkedIn.length,0), shooters:cards.reduce((sum,card)=>sum+card.members.length,0)};
}
